// components/settings/AddPaymentMethodModal.tsx
import React, { useState, useEffect } from "react";
import {
  X,
  Building2,
  CreditCard,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader2,
  AlertCircle,
  Shield,
  Mail,
  Hash,
  User,
  Ban,
} from "lucide-react";
import { AddPaymentMethodPayload } from "../../lib/types/settings";

interface AddPaymentMethodModalProps {
  onClose: () => void;
  onAdd: (payload: AddPaymentMethodPayload) => Promise<boolean>;
  isPending: boolean;
  existingTypes: ("bank" | "paypal")[];
}

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  onClose,
  onAdd,
  isPending,
  existingTypes,
}) => {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState<"bank" | "paypal" | null>(
    null
  );
  const [error, setError] = useState("");

  // Check which types are already added
  const hasBankAccount = existingTypes.includes("bank");
  const hasPayPal = existingTypes.includes("paypal");
  const allTypesAdded = hasBankAccount && hasPayPal;

  // Bank form state
  const [bankForm, setBankForm] = useState({
    bankName: "",
    accountType: "checking" as "checking" | "savings",
    accountNumber: "",
    routingNumber: "",
    accountHolderName: "",
  });

  // PayPal form state
  const [paypalForm, setPaypalForm] = useState({
    email: "",
    confirmEmail: "",
  });

  // Auto-select if only one type is available
  useEffect(() => {
    if (hasBankAccount && !hasPayPal) {
      setSelectedType("paypal");
    } else if (hasPayPal && !hasBankAccount) {
      setSelectedType("bank");
    }
  }, [hasBankAccount, hasPayPal]);

  const handleBankChange = (field: string, value: string) => {
    setBankForm((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handlePaypalChange = (field: string, value: string) => {
    setPaypalForm((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const validateBankForm = () => {
    if (!bankForm.bankName.trim()) return "Bank name is required";
    if (!bankForm.accountHolderName.trim())
      return "Account holder name is required";
    if (!bankForm.routingNumber.trim()) return "Routing number is required";
    if (bankForm.routingNumber.length !== 9)
      return "Routing number must be 9 digits";
    if (!bankForm.accountNumber.trim()) return "Account number is required";
    if (bankForm.accountNumber.length < 8)
      return "Account number must be at least 8 digits";
    return null;
  };

  const validatePaypalForm = () => {
    if (!paypalForm.email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalForm.email))
      return "Invalid email address";
    if (paypalForm.email !== paypalForm.confirmEmail)
      return "Emails do not match";
    return null;
  };

  const handleNext = () => {
    if (step === 1 && selectedType) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setError("");
    }
  };

  const handleSubmit = async () => {
    setError("");

    let payload: AddPaymentMethodPayload;

    if (selectedType === "bank") {
      const validationError = validateBankForm();
      if (validationError) {
        setError(validationError);
        return;
      }

      payload = {
        type: "bank",
        bankName: bankForm.bankName,
        accountType: bankForm.accountType,
        accountNumber: bankForm.accountNumber,
        routingNumber: bankForm.routingNumber,
        accountHolderName: bankForm.accountHolderName,
      };
    } else {
      const validationError = validatePaypalForm();
      if (validationError) {
        setError(validationError);
        return;
      }

      payload = {
        type: "paypal",
        paypalEmail: paypalForm.email,
      };
    }

    await onAdd(payload);
  };

  const renderStep1 = () => (
    <div>
      {!allTypesAdded && (
        <div className="|">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
            Choose Payment Method
          </h3>
          <p className="text-gray-500 text-sm mb-6 text-center">
            Select how you'd like to receive your payments
          </p>
        </div>
      )}

      {allTypesAdded ? (
        /* All Types Already Added */
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Ban className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            All Payment Methods Added
          </h4>
          <p className="text-gray-500 text-sm max-w-xs mx-auto">
            You already have both a bank account and PayPal connected. Remove an
            existing method to add a new one.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Bank Account Option */}
          <button
            onClick={() => !hasBankAccount && setSelectedType("bank")}
            disabled={hasBankAccount}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
              hasBankAccount
                ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
                : selectedType === "bank"
                  ? "border-[#8B52FF] bg-[#8B52FF]/5"
                  : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                hasBankAccount
                  ? "bg-gray-100"
                  : selectedType === "bank"
                    ? "bg-[#8B52FF]/10"
                    : "bg-blue-100"
              }`}
            >
              <Building2
                className={`w-6 h-6 ${
                  hasBankAccount
                    ? "text-gray-400"
                    : selectedType === "bank"
                      ? "text-[#8B52FF]"
                      : "text-blue-600"
                }`}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p
                  className={`font-semibold ${
                    hasBankAccount
                      ? "text-gray-400"
                      : selectedType === "bank"
                        ? "text-[#8B52FF]"
                        : "text-gray-900"
                  }`}
                >
                  Bank Account
                </p>
                {hasBankAccount && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    Added
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {hasBankAccount
                  ? "You already have a bank account connected"
                  : "Direct deposit • No fees • 1-3 business days"}
              </p>
            </div>
            {!hasBankAccount && selectedType === "bank" && (
              <CheckCircle className="w-5 h-5 text-[#8B52FF]" />
            )}
          </button>

          {/* PayPal Option */}
          <button
            onClick={() => !hasPayPal && setSelectedType("paypal")}
            disabled={hasPayPal}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
              hasPayPal
                ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
                : selectedType === "paypal"
                  ? "border-[#8B52FF] bg-[#8B52FF]/5"
                  : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                hasPayPal
                  ? "bg-gray-100"
                  : selectedType === "paypal"
                    ? "bg-[#8B52FF]/10"
                    : "bg-indigo-100"
              }`}
            >
              <CreditCard
                className={`w-6 h-6 ${
                  hasPayPal
                    ? "text-gray-400"
                    : selectedType === "paypal"
                      ? "text-[#8B52FF]"
                      : "text-indigo-600"
                }`}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p
                  className={`font-semibold ${
                    hasPayPal
                      ? "text-gray-400"
                      : selectedType === "paypal"
                        ? "text-[#8B52FF]"
                        : "text-gray-900"
                  }`}
                >
                  PayPal
                </p>
                {hasPayPal && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    Added
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {hasPayPal
                  ? "You already have PayPal connected"
                  : "Instant transfer • 0.5% fee • Same day"}
              </p>
            </div>
            {!hasPayPal && selectedType === "paypal" && (
              <CheckCircle className="w-5 h-5 text-[#8B52FF]" />
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderBankForm = () => (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
        Bank Account Details
      </h3>
      <p className="text-gray-500 text-sm mb-6 text-center">
        Enter your bank account information for direct deposits
      </p>

      <div className="space-y-4">
        {/* Bank Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Bank Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={bankForm.bankName}
              onChange={(e) => handleBankChange("bankName", e.target.value)}
              placeholder="e.g., Chase Bank"
              disabled={isPending}
              className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Account Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Account Type <span className="text-red-500">*</span>
          </label>
          <select
            value={bankForm.accountType}
            onChange={(e) =>
              handleBankChange(
                "accountType",
                e.target.value as "checking" | "savings"
              )
            }
            disabled={isPending}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all bg-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
          </select>
        </div>

        {/* Account Holder Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Account Holder Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={bankForm.accountHolderName}
              onChange={(e) =>
                handleBankChange("accountHolderName", e.target.value)
              }
              placeholder="Name on the account"
              disabled={isPending}
              className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Routing Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Routing Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={bankForm.routingNumber}
              onChange={(e) =>
                handleBankChange(
                  "routingNumber",
                  e.target.value.replace(/\D/g, "").slice(0, 9)
                )
              }
              placeholder="9-digit routing number"
              maxLength={9}
              disabled={isPending}
              className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all font-mono disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Account Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Account Number <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={bankForm.accountNumber}
              onChange={(e) =>
                handleBankChange(
                  "accountNumber",
                  e.target.value.replace(/\D/g, "").slice(0, 17)
                )
              }
              placeholder="Account number"
              disabled={isPending}
              className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all font-mono disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* Security Note */}
      <div className="mt-4 p-3 bg-gray-50 rounded-xl flex items-start gap-3">
        <Shield className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500">
          Your banking information is encrypted using bank-level security and is
          never shared with third parties.
        </p>
      </div>
    </div>
  );

  const renderPaypalForm = () => (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
        PayPal Details
      </h3>
      <p className="text-gray-500 text-sm mb-6 text-center">
        Enter the email address associated with your PayPal account
      </p>

      <div className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            PayPal Email <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={paypalForm.email}
              onChange={(e) => handlePaypalChange("email", e.target.value)}
              placeholder="your@email.com"
              disabled={isPending}
              className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Confirm Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Confirm Email <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={paypalForm.confirmEmail}
              onChange={(e) =>
                handlePaypalChange("confirmEmail", e.target.value)
              }
              placeholder="Confirm your email"
              disabled={isPending}
              className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      {/* Fee Note */}
      <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
        <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          PayPal transfers incur a 0.5% processing fee. For fee-free transfers,
          consider using a bank account.
        </p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isPending ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            Add Payment Method
          </h2>
          <button
            onClick={onClose}
            disabled={isPending}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {/* Step Indicator - Only show if not all types added */}
          {!allTypesAdded && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 1
                    ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                1
              </div>
              <div
                className={`w-12 h-1 rounded-full ${
                  step >= 2 ? "bg-[#8B52FF]" : "bg-gray-100"
                }`}
              />
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= 2
                    ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                2
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {step === 1 && renderStep1()}
          {step === 2 && selectedType === "bank" && renderBankForm()}
          {step === 2 && selectedType === "paypal" && renderPaypalForm()}
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
          {step > 1 ? (
            <button
              onClick={handleBack}
              disabled={isPending}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <div />
          )}

          {allTypesAdded ? (
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-all text-sm"
            >
              Close
            </button>
          ) : step === 1 ? (
            <button
              onClick={handleNext}
              disabled={!selectedType}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isPending}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  Add Payment Method
                  <CheckCircle className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPaymentMethodModal;
